import { c as _c } from "react-compiler-runtime";
import { OTPInput, OTPInputContext } from "input-otp";
import { Dot } from "lucide-react";
import * as React from "react";
import { cn } from "@/core/utils";
const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, React.ComponentPropsWithoutRef<typeof OTPInput>>((t0, ref) => {
  const $ = _c(13);
  let className;
  let containerClassName;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      containerClassName,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = containerClassName;
    $[3] = props;
  } else {
    className = $[1];
    containerClassName = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("disabled:cursor-not-allowed", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== containerClassName) {
    t2 = cn("flex items-center gap-2 has-[:disabled]:opacity-50", containerClassName);
    $[6] = containerClassName;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1 || $[11] !== t2) {
    t3 = <OTPInput className={t1} containerClassName={t2} ref={ref} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});
InputOTP.displayName = "InputOTP";
const InputOTPGroup = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <div className={t1} ref={ref} {...props} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
InputOTPGroup.displayName = "InputOTPGroup";
const InputOTPSlot = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div"> & {
  index: number;
}>((t0, ref) => {
  const $ = _c(15);
  let className;
  let index;
  let props;
  if ($[0] !== t0) {
    ({
      index,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = index;
    $[3] = props;
  } else {
    className = $[1];
    index = $[2];
    props = $[3];
  }
  const inputOTPContext = React.useContext(OTPInputContext);
  const {
    char,
    hasFakeCaret,
    isActive
  } = inputOTPContext.slots[index];
  const t1 = isActive && "ring-ring ring-offset-background z-10 ring-2";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn("relative flex h-12 w-12 items-center justify-center border-y border-r border-black/10 bg-white text-lg transition-all first:rounded-l-md first:border-l last:rounded-r-md", t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== hasFakeCaret) {
    t3 = hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="animate-caret-blink bg-foreground h-4 w-px duration-1000" /></div>;
    $[7] = hasFakeCaret;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== char || $[10] !== props || $[11] !== ref || $[12] !== t2 || $[13] !== t3) {
    t4 = <div className={t2} ref={ref} {...props}>{char}{t3}</div>;
    $[9] = char;
    $[10] = props;
    $[11] = ref;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
});
InputOTPSlot.displayName = "InputOTPSlot";
const InputOTPSeparator = React.forwardRef<React.ElementRef<"div">, React.ComponentPropsWithoutRef<"div">>((t0, ref) => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dot />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props || $[4] !== ref) {
    t2 = <div ref={ref} role="separator" {...props}>{t1}</div>;
    $[3] = props;
    $[4] = ref;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
});
InputOTPSeparator.displayName = "InputOTPSeparator";
export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };